// Notification Store - Manages toast notifications

import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Notification, NotificationOptions, NotificationType } from './types';
import { useDateFormat } from '@/composables/useDateFormat';

export const useNotificationStore = defineStore('notifications', () => {
  const notifications = ref<Notification[]>([]);
  const timeouts = new Map<string, ReturnType<typeof setTimeout>>();

  const dismiss = (id: string) => {
    const timeoutId = timeouts.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeouts.delete(id);
    }
    notifications.value = notifications.value.filter((n) => n.id !== id);
  };

  const clear = () => {
    timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    timeouts.clear();
    notifications.value = [];
  };

  const addNotification = (
    type: NotificationType,
    message: string,
    options?: NotificationOptions
  ) => {
    const { actions: dateActions } = useDateFormat();
    const notification: Notification = {
      id: crypto.randomUUID(),
      type,
      message,
      duration: options?.duration ?? 5000,
      dismissible: options?.dismissible ?? true,
      timestamp: dateActions.utcNowDate(),
    };

    notifications.value.push(notification);

    // Auto-dismiss if duration is set
    if (notification.duration && notification.duration > 0) {
      const timeoutId = setTimeout(() => {
        timeouts.delete(notification.id);
        dismiss(notification.id);
      }, notification.duration);
      timeouts.set(notification.id, timeoutId);
    }

    return notification.id;
  };

  const success = (message: string, options?: NotificationOptions) =>
    addNotification('success', message, options);

  const error = (message: string, options?: NotificationOptions) =>
    addNotification('error', message, options);

  const warning = (message: string, options?: NotificationOptions) =>
    addNotification('warning', message, options);

  const info = (message: string, options?: NotificationOptions) =>
    addNotification('info', message, options);

  return {
    notifications,
    addNotification,
    dismiss,
    clear,
    success,
    error,
    warning,
    info,
  };
});
